import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, Mail, MessageCircle, CheckCircle, AlertCircle } from 'lucide-react';
import {
  calculateCosts,
  CalculatorInputs,
  CalculatorResults,
  WATER_TYPE_OPTIONS,
  ROLE_OPTIONS,
} from '../utils/acidCostCalculator';
import { supabase } from '../utils/supabase';
import { submitToBlackOps, captureAttribution } from '../utils/leadCapture';
import TurnstileWidget from './TurnstileWidget';
import { useTurnstile } from '../hooks/useTurnstile';
import { TURNSTILE_REQUIRED_MESSAGE } from '../utils/turnstile';

interface AcidCostCalculatorProps {
  sourcePage: string;
}

const AcidCostCalculator: React.FC<AcidCostCalculatorProps> = ({ sourcePage }) => {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    annualWaterVolume: 100,
    annualWaterVolumeUnit: 'million_gallons',
    waterType: 'not_sure',
    sulfuricPrice: 450,
    sulfuricPriceUnit: 'per_ton',
    sulfurPricePerTon: 350,
    burnerPrice: undefined,
  });

  const [results, setResults] = useState<CalculatorResults | null>(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const turnstile = useTurnstile();
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [leadInfo, setLeadInfo] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    role: '',
  });

  useEffect(() => {
    const calculatedResults = calculateCosts(inputs);
    setResults(calculatedResults);
  }, [inputs]);

  const handleInputChange = (field: keyof CalculatorInputs, value: any) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  };

  const handleLeadInfoChange = (field: string, value: string) => {
    setLeadInfo((prev) => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!leadInfo.email) {
      setSubmitError('Email is required');
      return;
    }

    if (!leadInfo.firstName.trim()) {
      setSubmitError('Please enter your first name');
      return;
    }

    if (!leadInfo.lastName.trim()) {
      setSubmitError('Please enter your last name');
      return;
    }

    if (!results) {
      setSubmitError('Please complete the calculator first');
      return;
    }

    if (!turnstile.token) {
      setSubmitError(TURNSTILE_REQUIRED_MESSAGE);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const submissionData = {
        name: `${leadInfo.firstName.trim()} ${leadInfo.lastName.trim()}`,
        company: leadInfo.company || null,
        email: leadInfo.email,
        role: leadInfo.role || null,
        annual_water_volume: inputs.annualWaterVolume,
        annual_water_volume_unit: inputs.annualWaterVolumeUnit,
        water_type: inputs.waterType,
        sulfuric_price: inputs.sulfuricPrice,
        sulfuric_price_unit: inputs.sulfuricPriceUnit,
        sulfur_price_per_ton: inputs.sulfurPricePerTon,
        burner_price: inputs.burnerPrice || null,
        acid_cost: results.acidCost,
        sulfur_cost: results.sulfurCost,
        annual_savings: results.annualSavings,
        payback_years: results.paybackYears || null,
        source_page: sourcePage,
        user_agent: navigator.userAgent,
        ip_address: null,
        hubspot_submitted: false,
        hubspot_error: null,
      };

      const { data, error } = await supabase
        .from('acid_cost_calculator_submissions')
        .insert([submissionData])
        .select()
        .maybeSingle();

      if (error) {
        console.error('Database error:', error);
        throw new Error('Failed to save submission');
      }

      let ioSuccess = false;
      let ioError: string | null = null;

      try {
        const ioPayload = {
          form_type: 'calculator' as const,
          form_id: 'acid_cost_calculator',
          first_name: leadInfo.firstName.trim(),
          last_name: leadInfo.lastName.trim(),
          email: leadInfo.email,
          company_name: leadInfo.company || undefined,
          product_interest: 'Sulfur Burners',
          turnstile_token: turnstile.token,
          calculator_data: {
            annual_water_volume: inputs.annualWaterVolume,
            annual_water_volume_unit: inputs.annualWaterVolumeUnit,
            water_type: inputs.waterType,
            sulfuric_price: inputs.sulfuricPrice,
            sulfuric_price_unit: inputs.sulfuricPriceUnit,
            sulfur_price_per_ton: inputs.sulfurPricePerTon,
            estimated_acid_cost: results.acidCost,
            estimated_sulfur_cost: results.sulfurCost,
            estimated_annual_savings: results.annualSavings,
            estimated_payback_years: results.paybackYears || null,
            source_page: sourcePage,
          },
          ...captureAttribution(),
        };

        const ioResult = await submitToBlackOps(ioPayload);
        ioSuccess = ioResult.success;
        if (!ioResult.success) {
          ioError = ioResult.error || 'Unknown error';
        }
      } catch (ioErr: any) {
        console.error('Io submission error:', ioErr);
        ioError = ioErr.message;
      }

      if (data && data.id) {
        await supabase
          .from('acid_cost_calculator_submissions')
          .update({
            hubspot_submitted: ioSuccess,
            hubspot_error: ioError,
          })
          .eq('id', data.id);
      }

      setSubmitSuccess(true);
      setShowLeadForm(false);

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error: any) {
      console.error('Submission error:', error);
      setSubmitError(error.message || 'Failed to submit. Please try again.');
    } finally {
      // Turnstile tokens are single-use — clear it so a second submission works.
      turnstile.reset();
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden max-w-4xl mx-auto">
      <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-6">
        <div className="flex items-center space-x-2 mb-2">
          <Calculator size={24} />
          <h2 className="text-2xl font-bold">Acid Cost Calculator</h2>
        </div>
        <p className="text-white/90 text-sm">
          Compare sulfuric acid vs. elemental sulfur costs
        </p>
      </div>

      <div className="p-6">
        <div className="space-y-5">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Annual water volume
              </label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  value={inputs.annualWaterVolume}
                  onChange={(e) => handleInputChange('annualWaterVolume', parseFloat(e.target.value) || 0)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  min="0"
                  step="1"
                />
                <select
                  value={inputs.annualWaterVolumeUnit}
                  onChange={(e) => handleInputChange('annualWaterVolumeUnit', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="million_gallons">Million gallons/yr</option>
                  <option value="acre_feet">Acre-feet/yr</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Water type
              </label>
              <select
                value={inputs.waterType}
                onChange={(e) => handleInputChange('waterType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                {WATER_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Sulfuric acid price
              </label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  value={inputs.sulfuricPrice}
                  onChange={(e) => handleInputChange('sulfuricPrice', parseFloat(e.target.value) || 0)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  min="0"
                  step="1"
                />
                <select
                  value={inputs.sulfuricPriceUnit}
                  onChange={(e) => handleInputChange('sulfuricPriceUnit', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="per_ton">$/ton</option>
                  <option value="per_gallon">$/gallon</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Elemental sulfur price ($/ton)
              </label>
              <input
                type="number"
                value={inputs.sulfurPricePerTon}
                onChange={(e) => handleInputChange('sulfurPricePerTon', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                min="0"
                step="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Burner cost (optional)
              </label>
              <input
                type="number"
                value={inputs.burnerPrice || ''}
                onChange={(e) => handleInputChange('burnerPrice', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="e.g. 45,000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                min="0"
                step="1000"
              />
              <p className="text-xs text-gray-500 mt-1">
                For payback estimate
              </p>
            </div>
          </div>

          {results && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-5 border-2 border-blue-200"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Annual Cost Comparison
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="text-xs text-gray-600 mb-1">Sulfuric acid cost</div>
                  <div className="text-2xl font-bold text-red-600">{formatCurrency(results.acidCost)}</div>
                </div>

                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="text-xs text-gray-600 mb-1">Elemental sulfur cost</div>
                  <div className="text-2xl font-bold text-blue-600">{formatCurrency(results.sulfurCost)}</div>
                </div>

                <div className="bg-white rounded-lg p-4 shadow-sm border-2 border-green-500">
                  <div className="text-xs text-gray-600 mb-1">Annual savings</div>
                  <div className="text-2xl font-bold text-green-600">{formatCurrency(results.annualSavings)}</div>
                </div>
              </div>

              {results.paybackYears && (
                <div className="bg-white rounded-lg p-4 mb-4 border border-blue-200">
                  <p className="text-sm text-gray-700">
                    Payback in about <span className="font-semibold text-blue-600">{results.paybackYears} {results.paybackYears === 1 ? 'season' : 'seasons'}</span> from chemical savings alone
                  </p>
                </div>
              )}

              <div className="text-xs text-gray-600 italic bg-white rounded-lg p-3 mb-4">
                Directional estimate only. Actual design based on your lab report and system requirements.
              </div>

              <AnimatePresence>
                {submitSuccess && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-start space-x-2"
                  >
                    <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                    <div className="text-green-800">
                      <p className="font-medium text-sm">Success!</p>
                      <p className="text-xs">Results saved and emailed. Our team will follow up if it's a strong fit.</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {!showLeadForm && !submitSuccess && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setShowLeadForm(true)}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg inline-flex items-center justify-center space-x-2 text-sm"
                  >
                    <Mail size={16} />
                    <span>Email results</span>
                  </button>

                  <a
                    href="/contact"
                    className="flex-1 bg-white text-blue-600 px-5 py-2.5 rounded-lg font-semibold hover:bg-blue-50 transition-colors border-2 border-blue-600 inline-flex items-center justify-center space-x-2 text-sm"
                  >
                    <MessageCircle size={16} />
                    <span>Contact CTC</span>
                  </a>
                </div>
              )}

              <AnimatePresence>
                {showLeadForm && !submitSuccess && (
                  <motion.form
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    onSubmit={handleSubmit}
                    className="bg-white rounded-lg p-4 border border-blue-200 space-y-3"
                  >
                    <h4 className="text-base font-semibold text-gray-900 mb-2">
                      Email me these results
                    </h4>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={leadInfo.firstName}
                          onChange={(e) => handleLeadInfoChange('firstName', e.target.value)}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={leadInfo.lastName}
                          onChange={(e) => handleLeadInfoChange('lastName', e.target.value)}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Company
                      </label>
                      <input
                        type="text"
                        value={leadInfo.company}
                        onChange={(e) => handleLeadInfoChange('company', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={leadInfo.email}
                        onChange={(e) => handleLeadInfoChange('email', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Role
                      </label>
                      <select
                        value={leadInfo.role}
                        onChange={(e) => handleLeadInfoChange('role', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="">Select a role</option>
                        {ROLE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {submitError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                        <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
                        <div className="text-red-800 text-xs">{submitError}</div>
                      </div>
                    )}

                    <TurnstileWidget
                      ref={turnstile.ref}
                      onToken={turnstile.setToken}
                      action="acid_cost_calculator"
                    />

                    <div className="flex gap-3">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-5 py-2 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        {isSubmitting ? 'Sending...' : 'Send'}
                      </button>

                      <button
                        type="button"
                        onClick={() => setShowLeadForm(false)}
                        className="px-5 py-2 text-gray-600 hover:text-gray-800 font-medium text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcidCostCalculator;
